/**
 * Copyright (c) 2025 eBUCIS, LLC. All rights reserved.
 *
 * Version Service - Manages application version checking and updates
 *
 * Handles:
 * - BuildId comparison with server (format: YYYYMMDD.HHMMSS)
 * - Update priority levels (critical, recommended, optional)
 * - Auto-update when user is not logged in
 * - "Updating" state management
 *
 * This software is proprietary and confidential. Unauthorized copying,
 * distribution, or modification is strictly prohibited.
 */

import { versionConfig } from '../config/versionConfig'
import { createLogger } from '../utils/logger'

const logger = createLogger('versionService')

// Storage keys
const STORAGE_KEYS = {
  CURRENT_BUILD_ID: 'app_current_build_id',
  LAST_CHECK: 'app_version_last_check',
  DISMISSED_BUILD_ID: 'app_dismissed_build_id',
  IS_UPDATING: 'app_is_updating',
  UPDATE_STARTED_AT: 'app_update_started_at',
  RELOAD_COUNT: 'app_reload_count',
  RELOAD_WINDOW_START: 'app_reload_window_start'
}

// Timing constants
const TIMING = {
  UPDATE_TIMEOUT_MS: 60000, // Max time to wait for update to complete
  VISIBILITY_DEBOUNCE_MS: 500, // Debounce for visibility change checks
  RELOAD_WINDOW_MS: 5 * 60 * 1000, // 5 minute window for reload counting
  MAX_RELOADS_IN_WINDOW: 3, // Max reloads allowed in window before stopping
  FETCH_RETRY_ATTEMPTS: 3, // Number of retry attempts for failed fetches
  FETCH_RETRY_BASE_DELAY_MS: 1000 // Base delay for exponential backoff
}

// Update priority levels
export const UPDATE_PRIORITY = {
  CRITICAL: 'critical', // Security fixes, breaking changes - force immediate
  RECOMMENDED: 'recommended', // Features, bug fixes - show modal
  OPTIONAL: 'optional' // Minor tweaks - update silently when convenient
}

class VersionService {
  constructor() {
    this.currentBuildId = null
    this.serverBuildId = null
    this.serverData = null
    this.buildTime = null
    this.serverBuildTime = null
    this.checkingPromise = null // For request deduplication
    this.listeners = new Set()
    this.pollInterval = null
    this.isAuthenticated = false
    this.visibilityCheckTimeout = null
  }

  /**
   * Initialize the version service with the current build ID
   * Called at app startup
   */
  init() {
    // Skip version checking in development - builds change constantly via HMR
    if (import.meta.env.DEV) {
      logger.debug('[VersionService] Skipping in development mode')
      this.initialized = true
      return
    }

    // Get current build ID from build-time constants
    this.currentBuildId = versionConfig.buildId
    this.buildTime = versionConfig.buildTime

    // Store in localStorage for reference
    localStorage.setItem(STORAGE_KEYS.CURRENT_BUILD_ID, this.currentBuildId)

    // Check if we were in the middle of an update
    const wasUpdating = this.isUpdateInProgress()
    if (wasUpdating) {
      this.clearUpdateState()
      logger.debug('[VersionService] Update completed successfully')
    }

    logger.debug(`[VersionService] Initialized with build ${this.currentBuildId}`)
  }

  /**
   * Set authentication state - affects auto-update behavior
   */
  setAuthenticated(isAuthenticated) {
    this.isAuthenticated = isAuthenticated
  }

  /**
   * Check if an update is currently in progress
   */
  isUpdateInProgress() {
    const isUpdating = localStorage.getItem(STORAGE_KEYS.IS_UPDATING) === 'true'
    const startedAt = parseInt(localStorage.getItem(STORAGE_KEYS.UPDATE_STARTED_AT) || '0', 10)

    // If update started too long ago, assume it failed
    if (isUpdating && Date.now() - startedAt > TIMING.UPDATE_TIMEOUT_MS) {
      this.clearUpdateState()
      return false
    }

    return isUpdating
  }

  /**
   * Set update in progress state
   */
  setUpdateInProgress(inProgress) {
    if (inProgress) {
      localStorage.setItem(STORAGE_KEYS.IS_UPDATING, 'true')
      localStorage.setItem(STORAGE_KEYS.UPDATE_STARTED_AT, Date.now().toString())
    } else {
      this.clearUpdateState()
    }
  }

  /**
   * Clear update state
   */
  clearUpdateState() {
    localStorage.removeItem(STORAGE_KEYS.IS_UPDATING)
    localStorage.removeItem(STORAGE_KEYS.UPDATE_STARTED_AT)
  }

  /**
   * Sleep for a given duration
   * @param {number} ms - Milliseconds to sleep
   */
  sleep(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms))
  }

  /**
   * Fetch the current version from the server
   * Uses promise deduplication to prevent concurrent requests
   * Includes retry logic with exponential backoff
   * @returns {Promise<Object>} Server version info
   */
  fetchServerVersion() {
    // Return existing promise if a request is in flight (deduplication)
    if (this.checkingPromise) {
      return this.checkingPromise
    }

    this.checkingPromise = (async () => {
      for (let attempt = 0; attempt < TIMING.FETCH_RETRY_ATTEMPTS; attempt++) {
        try {
          // Add cache-busting query param
          const response = await fetch(`/meta.json?t=${Date.now()}`, {
            cache: 'no-store',
            headers: {
              'Cache-Control': 'no-cache',
              Pragma: 'no-cache'
            }
          })

          if (!response.ok) {
            throw new Error(`Failed to fetch version: ${response.status}`)
          }

          const data = await response.json()

          this.serverBuildId = data.buildId
          this.serverBuildTime = data.buildTime
          this.serverData = data

          localStorage.setItem(STORAGE_KEYS.LAST_CHECK, Date.now().toString())

          logger.debug(
            `[VersionService] Server build: ${this.serverBuildId}, ` +
              `priority: ${data.updatePriority}`
          )

          return data
        } catch (error) {
          const isLastAttempt = attempt === TIMING.FETCH_RETRY_ATTEMPTS - 1

          if (isLastAttempt) {
            logger.error('[VersionService] Failed to fetch server version after retries:', error)
          } else {
            // Exponential backoff: 1s, 2s, 4s...
            const delay = TIMING.FETCH_RETRY_BASE_DELAY_MS * Math.pow(2, attempt)
            logger.warn(
              `[VersionService] Fetch attempt ${attempt + 1} failed, retrying in ${delay}ms:`,
              error.message
            )
            await this.sleep(delay)
          }
        }
      }

      return null
    })().finally(() => {
      this.checkingPromise = null
    })

    return this.checkingPromise
  }

  /**
   * Compare two build IDs (format: YYYYMMDD.HHMMSS)
   * Simple string comparison works for chronological ordering
   * @param {string} a - First build ID
   * @param {string} b - Second build ID
   * @returns {number} -1 if a < b, 0 if equal, 1 if a > b
   */
  compareBuildIds(a, b) {
    // Handle null/undefined - treat missing build ID as needing update
    if (!a && !b) return 0
    if (!a) return -1 // No local build means we need to update
    if (!b) return 1 // No server build means no update available

    // Simple string comparison works for YYYYMMDD.HHMMSS format
    if (a < b) return -1
    if (a > b) return 1
    return 0
  }

  /**
   * Determine if update should be forced based on priority
   */
  shouldForceUpdate(serverData) {
    if (!serverData) return false

    // Force on critical updates
    if (serverData.updatePriority === UPDATE_PRIORITY.CRITICAL) {
      logger.debug('[VersionService] Critical update, forcing update')
      return true
    }

    return false
  }

  /**
   * Check if there is a new version available
   * @param {Object} options - Check options
   * @param {boolean} options.autoUpdateIfLoggedOut - Auto-update if user not logged in
   * @returns {Promise<Object|null>} Update info or null if no update
   */
  async checkForUpdate(options = {}) {
    // Skip in development mode
    if (import.meta.env.DEV) {
      return null
    }

    const { autoUpdateIfLoggedOut = true } = options

    const serverData = await this.fetchServerVersion()

    if (!serverData) {
      return null
    }

    const comparison = this.compareBuildIds(this.currentBuildId, serverData.buildId)

    if (comparison < 0) {
      // Server has newer build
      const forceUpdate = this.shouldForceUpdate(serverData)

      const updateInfo = {
        currentBuildId: this.currentBuildId,
        newBuildId: serverData.buildId,
        buildTime: serverData.buildTime,
        updatePriority: serverData.updatePriority || UPDATE_PRIORITY.RECOMMENDED,
        forceUpdate,
        changelog: serverData.changelog || null
      }

      logger.debug('[VersionService] Update available:', updateInfo)

      // Auto-update if:
      // 1. User is not authenticated AND
      // 2. autoUpdateIfLoggedOut is true AND
      // 3. (forceUpdate OR priority is optional)
      if (autoUpdateIfLoggedOut && !this.isAuthenticated) {
        if (forceUpdate || serverData.updatePriority === UPDATE_PRIORITY.OPTIONAL) {
          logger.debug('[VersionService] Auto-updating (user not logged in)')
          this.hardReload()
          return updateInfo // Won't actually return due to reload
        }
      }

      // Notify listeners
      this.notifyListeners(updateInfo)

      return updateInfo
    }

    return null
  }

  /**
   * Check if the user has dismissed this build update
   */
  isDismissed(buildId) {
    const dismissed = localStorage.getItem(STORAGE_KEYS.DISMISSED_BUILD_ID)
    return dismissed === buildId
  }

  /**
   * Dismiss the update notification for a specific build
   * Only works for non-critical, non-forced updates
   */
  dismissUpdate(buildId) {
    localStorage.setItem(STORAGE_KEYS.DISMISSED_BUILD_ID, buildId)
  }

  /**
   * Clear dismissed build (e.g., when a new build is released)
   */
  clearDismissed() {
    localStorage.removeItem(STORAGE_KEYS.DISMISSED_BUILD_ID)
  }

  /**
   * Start polling for version updates
   * Uses different intervals based on authentication state
   */
  startPolling() {
    // Guard against multiple registrations
    if (this.pollInterval) {
      return
    }

    // Use longer interval when authenticated (user is active)
    // Use shorter interval when not authenticated (login page, can update freely)
    const interval = this.isAuthenticated
      ? versionConfig.authenticatedCheckInterval
      : versionConfig.unauthenticatedCheckInterval

    logger.debug(
      `[VersionService] Starting version polling every ${interval / 1000}s ` +
        `(authenticated: ${this.isAuthenticated})`
    )

    this.pollInterval = setInterval(() => {
      this.checkForUpdate()
    }, interval)

    // Also check on visibility change
    document.addEventListener('visibilitychange', this.handleVisibilityChange)
  }

  /**
   * Stop polling for version updates
   */
  stopPolling() {
    if (this.pollInterval) {
      clearInterval(this.pollInterval)
      this.pollInterval = null
    }
    // Clear any pending visibility check timeout to prevent memory leak
    if (this.visibilityCheckTimeout) {
      clearTimeout(this.visibilityCheckTimeout)
      this.visibilityCheckTimeout = null
    }
    document.removeEventListener('visibilitychange', this.handleVisibilityChange)
  }

  /**
   * Handle visibility change - check for updates when tab becomes visible
   * Debounced to prevent multiple API calls on rapid tab switching
   */
  handleVisibilityChange = () => {
    if (!document.hidden) {
      // Debounce: clear any pending check and schedule a new one
      if (this.visibilityCheckTimeout) {
        clearTimeout(this.visibilityCheckTimeout)
      }

      this.visibilityCheckTimeout = setTimeout(() => {
        // Check if enough time has passed since last check
        const lastCheck = parseInt(localStorage.getItem(STORAGE_KEYS.LAST_CHECK) || '0', 10)
        const minInterval = versionConfig.minCheckInterval

        if (Date.now() - lastCheck >= minInterval) {
          this.checkForUpdate()
        }
        this.visibilityCheckTimeout = null
      }, TIMING.VISIBILITY_DEBOUNCE_MS)
    }
  }

  /**
   * Subscribe to version update notifications
   * @param {Function} callback - Called when update is available
   * @returns {Function} Unsubscribe function
   */
  subscribe(callback) {
    this.listeners.add(callback)
    return () => this.listeners.delete(callback)
  }

  /**
   * Notify all listeners of an update
   */
  notifyListeners(updateInfo) {
    this.listeners.forEach((callback) => {
      try {
        callback(updateInfo)
      } catch (error) {
        logger.error('[VersionService] Listener error:', error)
      }
    })
  }

  /**
   * Check if we've hit the reload limit to prevent infinite reload loops
   * @returns {boolean} True if it's safe to reload
   */
  canReload() {
    const now = Date.now()
    const windowStart = parseInt(localStorage.getItem(STORAGE_KEYS.RELOAD_WINDOW_START) || '0', 10)
    const reloadCount = parseInt(localStorage.getItem(STORAGE_KEYS.RELOAD_COUNT) || '0', 10)

    // Reset counter if window has passed (count this reload as the first in new window)
    if (now - windowStart > TIMING.RELOAD_WINDOW_MS) {
      localStorage.setItem(STORAGE_KEYS.RELOAD_WINDOW_START, now.toString())
      localStorage.setItem(STORAGE_KEYS.RELOAD_COUNT, '1')
      return true
    }

    // Check if we've hit the limit
    if (reloadCount >= TIMING.MAX_RELOADS_IN_WINDOW) {
      logger.error(
        `[VersionService] Reload limit reached (${TIMING.MAX_RELOADS_IN_WINDOW} in ${TIMING.RELOAD_WINDOW_MS / 1000}s). ` +
          'Preventing infinite reload loop. Please try clearing your cache manually.'
      )
      return false
    }

    return true
  }

  /**
   * Increment the reload counter
   */
  incrementReloadCount() {
    const now = Date.now()
    const windowStart = parseInt(localStorage.getItem(STORAGE_KEYS.RELOAD_WINDOW_START) || '0', 10)

    // Reset if window has passed
    if (now - windowStart > TIMING.RELOAD_WINDOW_MS) {
      localStorage.setItem(STORAGE_KEYS.RELOAD_WINDOW_START, now.toString())
      localStorage.setItem(STORAGE_KEYS.RELOAD_COUNT, '1')
    } else {
      const count = parseInt(localStorage.getItem(STORAGE_KEYS.RELOAD_COUNT) || '0', 10)
      localStorage.setItem(STORAGE_KEYS.RELOAD_COUNT, (count + 1).toString())
    }
  }

  /**
   * Perform a hard reload, clearing caches
   * Sets updating state so UI can show appropriate message
   * Returns false if reload was prevented due to limit
   *
   * Integrates with PWA service worker to ensure:
   * - Any waiting service worker is activated
   * - All caches (browser + SW) are cleared
   */
  async hardReload() {
    // Check reload limit to prevent infinite loops
    if (!this.canReload()) {
      this.clearUpdateState()
      return false
    }

    logger.debug('[VersionService] Performing hard reload...')

    // Increment reload counter
    this.incrementReloadCount()

    // Set updating state before reload
    this.setUpdateInProgress(true)

    // Clear localStorage version tracking (but keep IS_UPDATING and reload tracking)
    localStorage.removeItem(STORAGE_KEYS.CURRENT_BUILD_ID)
    localStorage.removeItem(STORAGE_KEYS.LAST_CHECK)
    localStorage.removeItem(STORAGE_KEYS.DISMISSED_BUILD_ID)

    // Clear all caches to ensure fresh content
    // Note: SW cleanup is now handled in index.html for all page loads
    if ('caches' in window) {
      try {
        const names = await caches.keys()
        await Promise.all(names.map((name) => caches.delete(name)))
        logger.debug('[VersionService] Cleared browser caches')
      } catch (err) {
        logger.warn('[VersionService] Cache clearing failed:', err)
        // Continue with reload even if cache clearing fails
      }
    }

    // Force reload with cache-busting URL to ensure fresh content
    const url = new URL(window.location.href)
    url.searchParams.set('_reload', Date.now().toString())
    window.location.replace(url.toString())
    return true
  }

  /**
   * Get the current version info
   */
  getVersionInfo() {
    return {
      currentBuildId: this.currentBuildId,
      serverBuildId: this.serverBuildId,
      buildTime: this.buildTime,
      serverBuildTime: this.serverBuildTime,
      serverData: this.serverData
    }
  }
}

// Export singleton instance
const versionService = new VersionService()
export default versionService
